import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Check, X, ExternalLink, RefreshCw, Loader2, Hammer, LogOut, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import AdminTabs from "@/components/AdminTabs";

const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString("en-CA", { month: "short", day: "numeric" }) : "—");

const Chip = ({ label, active, onClick, testId }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testId}
    className={`px-3 py-1.5 text-[11px] font-archivo uppercase tracking-widest border ${active ? "bg-bb-navy text-white border-bb-navy" : "bg-white text-bb-navy border-border"}`}
  >
    {label}
  </button>
);

export default function AdminFilingsPage() {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("new");
  const [source, setSource] = useState("all");
  const [sources, setSources] = useState([]); // catalogue from /admin/ingest/sources
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(null); // source_id currently running, else null
  const [showSourceMenu, setShowSourceMenu] = useState(false);
  const [busy, setBusy] = useState({});

  const load = () => {
    setLoading(true);
    const params = new URLSearchParams({ status });
    if (source !== "all") params.set("source", source);
    api.get(`/admin/filings?${params.toString()}`, { withCredentials: true })
      .then((r) => setItems(Array.isArray(r.data) ? r.data : []))
      .catch(() => toast.error("Failed to load filings"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    api.get("/admin/ingest/sources", { withCredentials: true })
      .then((r) => setSources(Array.isArray(r.data) ? r.data : []))
      .catch(() => { /* ignore — dropdown will show a friendly empty state */ });
  }, []);

  useEffect(() => { load(); }, [status, source]);

  const runIngest = async (sourceId) => {
    setShowSourceMenu(false);
    setRunning(sourceId);
    const label = sources.find((s) => s.source_id === sourceId)?.label || sourceId;
    try {
      const r = await api.post(`/admin/ingest/${sourceId}/run`, null, { withCredentials: true });
      if (r.status === 202) {
        toast.info(`${label} ingest started in background. Fetching updates…`);
      } else {
        toast.success(`${label} ingest done`);
        load();
        setRunning(null);
        return;
      }
    } catch (e) {
      if (e?.response?.status === 409) {
        toast.error(`${label} is already running. Please wait.`);
      } else {
        toast.error(`Ingest failed for ${label}`);
      }
      setRunning(null);
      return;
    }

    // Poll status every 5s until is_running=false, then refresh the list.
    let elapsed = 0;
    const pollMs = 5000;
    const maxMs = 5 * 60 * 1000; // 5 min hard cap
    const poll = async () => {
      try {
        const s = await api.get(`/admin/ingest/${sourceId}/status`, { withCredentials: true });
        if (!s.data.is_running) {
          const lr = s.data.last_run || {};
          toast.success(
            `${label} ingest · new ${lr.items_new ?? 0} · updated ${lr.items_updated ?? 0}` +
            (lr.budget_exhausted ? " (LLM budget hit)" : ""),
          );
          setRunning(null);
          load();
          return;
        }
      } catch { /* transient — keep polling */ }
      elapsed += pollMs;
      if (elapsed >= maxMs) {
        toast.warning(`${label} still running — check back shortly.`);
        setRunning(null);
        return;
      }
      setTimeout(poll, pollMs);
    };
    setTimeout(poll, pollMs);
  };

  const act = async (id, action) => {
    setBusy((b) => ({ ...b, [id]: true }));
    try {
      await api.post(`/admin/filings/${id}/${action}`, null, { withCredentials: true });
      toast.success(action === "approve" ? "Approved → published" : "Rejected");
      setItems((xs) => xs.filter((x) => x.filing_id !== id));
    } catch { toast.error(`Failed to ${action}`); }
    finally { setBusy((b) => ({ ...b, [id]: false })); }
  };

  const logout = async () => {
    try { await api.post("/auth/logout", null, { withCredentials: true }); } catch { /* ignore */ }
    window.location.href = "/login";
  };

  return (
    <div data-testid="admin-filings-page" className="bg-bb-offwhite min-h-screen">
      <header className="sticky top-0 z-40 bg-white border-b border-border">
        <div className="bb-container py-3 flex items-center justify-between gap-3">
          <div>
            <div className="bb-overline text-bb-copper">Admin · Filings</div>
            <div className="font-archivo font-bold text-bb-navy text-lg leading-tight">Triage — {items.length} items</div>
          </div>
          <div className="flex items-center gap-2 relative">
            <button
              type="button"
              onClick={() => setShowSourceMenu((v) => !v)}
              disabled={running !== null || sources.length === 0}
              data-testid="admin-run-ingest"
              className="bb-btn-outline inline-flex items-center gap-2 px-3 py-2 text-[11px] disabled:opacity-60"
            >
              {running ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />}
              <span className="hidden sm:inline">
                {running ? `Running ${sources.find((s) => s.source_id === running)?.label || running}…` : "Run ingest"}
              </span>
              <ChevronDown size={12} />
            </button>
            {showSourceMenu && (
              <div
                data-testid="admin-run-ingest-menu"
                className="absolute right-0 top-full mt-1 z-50 bg-white border border-border shadow-lg min-w-[200px]"
              >
                {sources.map((s) => (
                  <button
                    key={s.source_id}
                    onClick={() => runIngest(s.source_id)}
                    data-testid={`admin-run-ingest-${s.source_id}`}
                    className="block w-full text-left px-3 py-2.5 text-xs font-archivo hover:bg-bb-offwhite border-b border-border last:border-b-0"
                  >
                    <span className="font-semibold text-bb-navy">{s.label}</span>
                    <span className="text-bb-slate ml-2 text-[10px]">{s.jurisdiction}</span>
                  </button>
                ))}
                {sources.length === 0 && (
                  <div className="px-3 py-2 text-xs text-bb-slate">No sources registered</div>
                )}
              </div>
            )}
            <button onClick={logout} data-testid="admin-logout"
              className="inline-flex items-center gap-1 px-3 py-2 text-[11px] font-archivo uppercase tracking-widest text-bb-slate hover:text-bb-navy">
              <LogOut size={13} /> <span className="hidden sm:inline">Sign out</span>
            </button>
          </div>
        </div>
        <AdminTabs />
        <div className="bb-container py-2 flex gap-2 overflow-x-auto">
          {[["new", "New"], ["approved", "Approved"], ["rejected", "Rejected"], ["all", "All"]].map(([v, l]) => (
            <Chip key={v} label={l} active={status === v} onClick={() => setStatus(v)} testId={`admin-status-${v}`} />
          ))}
          <span className="w-px h-6 bg-border mx-1 self-center" aria-hidden />
          <Chip label="All sources" active={source === "all"} onClick={() => setSource("all")} testId="admin-source-all" />
          {sources.map((s) => (
            <Chip
              key={s.source_id}
              label={s.label}
              active={source === s.source_id}
              onClick={() => setSource(s.source_id)}
              testId={`admin-source-${s.source_id}`}
            />
          ))}
        </div>
      </header>

      <div className="bb-container py-4 space-y-3">
        {loading ? (
          <div className="text-center py-10 bb-overline text-bb-slate">Loading…</div>
        ) : items.length === 0 ? (
          <div className="bg-white border border-border p-8 text-center">
            <Hammer size={20} className="text-bb-copper mx-auto" />
            <div className="bb-overline text-bb-copper mt-3">Inbox zero</div>
            <p className="text-sm text-bb-slate mt-2">No {status} filings. Try &ldquo;Run ingest&rdquo; above to pull the latest.</p>
          </div>
        ) : items.map((f) => {
          const t = f.tags || {};
          const isBusy = !!busy[f.filing_id];
          return (
            <article key={f.filing_id} className="bg-white border border-border p-4 flex flex-col gap-3" data-testid={`admin-filing-${f.filing_id}`}>
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="bb-overline text-bb-copper">{f.jurisdiction}</span>
                  {f.source && (
                    <span
                      className="text-[10px] font-mono px-2 py-0.5 bg-bb-navy text-white"
                      data-testid={`filing-source-${f.filing_id}`}
                    >
                      {(sources.find((s) => s.source_id === f.source)?.label || f.source).toUpperCase()}
                    </span>
                  )}
                  <span className="bb-tag-neutral text-[10px] font-mono px-2 py-0.5">{f.status || "—"}</span>
                  {t.confidence > 0 && (
                    <span className={`text-[10px] font-mono px-2 py-0.5 ${t.confidence >= 0.7 ? "bb-tag-positive" : "bb-tag-neutral"}`}>
                      LLM {(t.confidence * 100).toFixed(0)}%
                    </span>
                  )}
                </div>
                <span className="text-[11px] font-mono text-bb-slate">{fmt(f.effective_date)}</span>
              </div>
              <h3 className="font-archivo text-base sm:text-lg text-bb-navy font-semibold leading-snug">{f.title}</h3>
              {t.one_line_summary && (
                <p className="text-sm text-bb-slate leading-relaxed border-l-2 border-bb-copper pl-3">
                  <span className="bb-overline text-bb-copper">LLM · </span>{t.one_line_summary}
                </p>
              )}
              <p className="text-xs text-bb-slate leading-relaxed">{f.summary}</p>
              <div className="text-[11px] font-mono text-bb-slate flex flex-wrap gap-x-3 gap-y-1">
                {t.project_id && <span>project: {t.project_id}</span>}
                {t.company_id && <span>company: {t.company_id}</span>}
                {t.region_id && <span>region: {t.region_id}</span>}
                {t.commodity_id && <span>commodity: {t.commodity_id}</span>}
              </div>
              <div className="flex items-center justify-between gap-2 pt-3 border-t border-border">
                <a href={f.registry_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[11px] font-mono text-bb-navy bb-link">
                  <ExternalLink size={11} /> Source
                </a>
                {status === "new" && (
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => act(f.filing_id, "reject")} disabled={isBusy}
                      data-testid={`filing-${f.filing_id}-reject`}
                      className="inline-flex items-center gap-1 px-4 py-2.5 border border-border text-bb-slate text-sm font-archivo uppercase tracking-widest disabled:opacity-50 min-h-[44px]">
                      <X size={14} /> Reject
                    </button>
                    <button
                      onClick={() => act(f.filing_id, "approve")} disabled={isBusy}
                      data-testid={`filing-${f.filing_id}-approve`}
                      className="bb-btn-primary inline-flex items-center gap-1 px-4 py-2.5 text-sm disabled:opacity-50 min-h-[44px]">
                      {isBusy ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />} Approve
                    </button>
                  </div>
                )}
              </div>
            </article>
          );
        })}
      </div>

      <div className="bb-container py-6 text-center">
        <Link to="/" className="text-xs font-archivo uppercase tracking-widest text-bb-slate bb-link">Back to site</Link>
      </div>
    </div>
  );
}
